import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Activity, FileText, Mail, Lock, ChevronLeft, FlaskConical, ShieldCheck, Brain, Database, BarChart3 } from "lucide-react";
import { AmdirisLogo } from "@/components/AmdirisLogo";

import { API } from "@/lib/api-base";

const features = [
  { icon: FileText, title: "eCRF Data Capture", desc: "Build and fill electronic case report forms" },
  { icon: Activity, title: "Real-World Evidence", desc: "Multi-site clinical study management" },
  { icon: Brain, title: "AI-Assisted Validation", desc: "Smart edit checks and anomaly detection" },
  { icon: ShieldCheck, title: "21 CFR Part 11 Audit Trail", desc: "Tamper-evident logs and e-signatures" },
  { icon: Database, title: "Bank-Grade Data Security", desc: "Encrypted storage with role-based access" },
  { icon: BarChart3, title: "Real-Time Analytics", desc: "Live dashboards across sites and visits" },
];

const errMsg = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || fallback;
};

export default function LoginPage() {
  const [step, setStep] = useState("email"); // email | studyPick | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [studyChoices, setStudyChoices] = useState([]); // [{study_id, study_name, role}]
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const goBackToEmail = () => {
    setStep("email"); setPassword(""); setError(""); setInfo("");
    setStudyChoices([]); setSelectedStudyId("");
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/method`, { params: { email: email.toLowerCase() } });
      // Phase 2.30: password-only auth. If the email maps to multiple studies, show picker
      // first so the JWT can be scoped to the chosen study. Otherwise go straight to password.
      const studies = data.studies || [];
      if (studies.length > 1) {
        setStudyChoices(studies);
        setStep("studyPick");
      } else {
        if (studies.length === 1) setSelectedStudyId(studies[0].study_id);
        setStep("password");
      }
    } catch (err) {
      setError(errMsg(err, "Unable to verify account. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleStudyPick = (studyId) => {
    setSelectedStudyId(studyId);
    setStep("password");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const body = { email: email.toLowerCase(), password };
      if (selectedStudyId) body.study_id = selectedStudyId;
      const { data } = await axios.post(`${API}/auth/login`, body, { withCredentials: true });
      setUser(data);
      // Phase 2.43: admin set a temp password — force the user to change it now
      if (data?.must_change_password) {
        navigate("/change-password?force=1");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(errMsg(err, "Invalid credentials. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const stepIcon = step === "password" ? Lock : step === "studyPick" ? FlaskConical : Mail;
  const StepIcon = stepIcon;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(135deg, #060D1A 0%, #0A1628 50%, #0D1F3C 100%)" }}>
      {/* ============== TOP HEADER BAR ============== */}
      <header className="w-full px-6 sm:px-12 py-5 flex items-center"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        {/* Logo + wordmark — same height per user request */}
        <div className="flex items-center gap-3" data-testid="brand-lockup">
          <AmdirisLogo size={48} />
          <span className="text-3xl sm:text-4xl font-bold tracking-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}>
            <span className="text-white">AMD</span><span className="iris-gradient">iris</span>
          </span>
        </div>
      </header>

      {/* ============== BODY ============== */}
      <div className="flex-1 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 px-12 py-10" style={{ borderRight: "1px solid rgba(255,255,255,0.05)" }}>
        <div>
          {/* Hero tagline */}
          <h1 className="text-5xl sm:text-6xl font-bold text-white leading-[1.05] mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            EDC Platform for<br />
            <span className="iris-gradient">Real-World Evidence Studies</span>
          </h1>
          <p className="text-base text-slate-400 leading-relaxed max-w-lg mb-10">
            An Electronic Data Capture platform that is purpose-built for single
            and multi-site Real-World Evidence (RWE) Studies.
          </p>

          {/* Compact 2-column feature grid */}
          <div className="grid grid-cols-2 gap-3 max-w-2xl">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-3 p-3 rounded-xl glass-card fade-in">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(0,180,216,0.15)" }}>
                  <f.icon size={15} color="#00B4D8" strokeWidth={1.7} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white truncate">{f.title}</p>
                  <p className="text-[10.5px] text-slate-400 mt-0.5 leading-tight">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-600 mt-8">
          &copy; 2026 AMDiris. All rights reserved.
        </p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Trust tag — center-aligned above the sign-in card */}
          <div className="mb-5 flex justify-center"
               data-testid="login-trust-tag">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                 style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)" }}>
              <ShieldCheck size={15} color="#10B981" strokeWidth={2} />
              <p className="text-xs font-medium tracking-wide text-emerald-300">
                Secure Healthcare Research Platform
              </p>
            </div>
          </div>

          <div className="p-8 rounded-2xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(16px)" }}>
            <div className="mb-6 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(0,180,216,0.12)" }}>
                <StepIcon size={20} color="#00B4D8" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {step === "email" && "Sign in to AMDiris"}
                  {step === "studyPick" && "Select a study"}
                  {step === "password" && "Enter your password"}
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {step === "email" && "Access your clinical research dashboard"}
                  {step === "studyPick" && "Multiple study logins are tied to this email"}
                  {step === "password" && email}
                </p>
              </div>
            </div>

            {/* STEP 1: EMAIL */}
            {step === "email" && (
              <form onSubmit={handleEmailSubmit} className="space-y-5" data-testid="login-form">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-widest">Email Address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                         placeholder="you@amdiris.com" required autoFocus
                         autoComplete="username"
                         name="email"
                         data-testid="login-email-input"
                         className="amdiris-input w-full px-4 py-3 text-sm" />
                </div>
                {error && <ErrorBox text={error} />}
                <PrimaryButton loading={loading} testId="login-continue-btn">Continue</PrimaryButton>
              </form>
            )}

            {/* STEP 1B: STUDY PICKER (when same email is registered for multiple studies) */}
            {step === "studyPick" && (
              <div className="space-y-3" data-testid="login-study-picker">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">Choose which study session to enter</p>
                  <button type="button" onClick={goBackToEmail} className="text-xs text-[#00B4D8] hover:underline flex items-center gap-1" data-testid="login-back-btn">
                    <ChevronLeft size={12} /> Change email
                  </button>
                </div>
                {studyChoices.map((s) => (
                  <button key={s.study_id} type="button" onClick={() => handleStudyPick(s.study_id)}
                          disabled={loading}
                          data-testid={`study-pick-${s.study_id}`}
                          className="w-full p-4 rounded-lg text-left transition-all flex items-center justify-between group"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    <div>
                      <p className="text-sm font-semibold text-white">{s.study_name}</p>
                      <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-wider">{s.role}</p>
                    </div>
                    <ChevronLeft size={16} className="text-[#00B4D8] rotate-180 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
                {error && <ErrorBox text={error} />}
              </div>
            )}

            {/* STEP 2A: PASSWORD */}
            {step === "password" && (
              <form onSubmit={handlePasswordSubmit} className="space-y-5" data-testid="login-form">
                {/* Hidden username for password manager autofill context (Phase 2.28) */}
                <input type="text" name="username" value={email} readOnly
                       autoComplete="username"
                       tabIndex={-1}
                       style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                       aria-hidden="true"
                       data-testid="login-hidden-username" />
                {selectedStudyId && studyChoices.find(s => s.study_id === selectedStudyId) && (
                  <div className="px-3 py-2 rounded-lg text-xs flex items-center gap-2"
                       style={{ background: "rgba(0,180,216,0.06)", border: "1px solid rgba(0,180,216,0.15)" }}
                       data-testid="study-context-banner">
                    <FlaskConical size={12} color="#00B4D8" />
                    <span className="text-slate-300">Logging in for: <span className="text-white font-medium">{studyChoices.find(s => s.study_id === selectedStudyId)?.study_name}</span></span>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-widest">Password</label>
                    <button type="button" onClick={goBackToEmail} className="text-xs text-[#00B4D8] hover:underline flex items-center gap-1" data-testid="login-back-btn">
                      <ChevronLeft size={12} /> Change email
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showPwd ? "text" : "password"} value={password}
                           onChange={e => setPassword(e.target.value)}
                           placeholder="Enter your password" required autoFocus
                           autoComplete="current-password"
                           name="password"
                           data-testid="login-password-input"
                           className="amdiris-input w-full px-4 py-3 text-sm pr-12" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
                      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && <ErrorBox text={error} />}
                {/* Phase 2.46.166 — Multi-study self-serve nudge. When a
                    doctor on 2+ studies fails password verification,
                    prompt them to double-check that they're on the
                    right study (each study has its own auto-generated
                    password now). Purely UX — no data leaked (we only
                    surface information the user already saw on the
                    picker). */}
                {error && studyChoices.length > 1 && (
                  <div className="px-3 py-2 rounded-lg text-xs flex items-start gap-2"
                       data-testid="multi-study-password-hint"
                       style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
                    <FlaskConical size={12} color="#F59E0B" className="mt-0.5 flex-shrink-0" />
                    <span className="text-amber-200/90">
                      You're enrolled in <span className="font-semibold text-amber-100">{studyChoices.length}</span> studies —
                      each has its own password. Make sure you're logging into
                      the right one.{" "}
                      <button type="button" onClick={goBackToEmail}
                              data-testid="multi-study-password-hint-switch"
                              className="underline text-amber-100 hover:text-white">
                        Switch study
                      </button>
                    </span>
                  </div>
                )}
                <PrimaryButton loading={loading} testId="login-submit-btn">Sign In</PrimaryButton>
                <button type="button" onClick={() => navigate("/forgot-password")}
                        data-testid="login-forgot-password-link"
                        className="w-full text-center text-xs text-[#00B4D8] hover:underline">
                  Forgot password?
                </button>
              </form>
            )}

            <div className="mt-6 pt-5 border-t border-white/5 text-center">
              <p className="text-xs text-slate-500">Need access? Contact your system administrator.</p>
              <p className="text-xs text-slate-600 mt-2">Browser will offer to save your credentials — accept it to sign in instantly next time.</p>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-slate-600">Secured by 256-bit AES encryption</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// --- helper sub-components ---
function PrimaryButton({ children, loading, testId }) {
  return (
    <button type="submit" disabled={loading} data-testid={testId}
            className="w-full py-3 rounded-lg font-semibold text-white transition-all duration-200 text-sm"
            style={{
              background: loading ? "rgba(0,180,216,0.5)" : "linear-gradient(135deg, #00B4D8, #48CAE4)",
              cursor: loading ? "not-allowed" : "pointer"
            }}>
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
          Authenticating...
        </span>
      ) : children}
    </button>
  );
}

function ErrorBox({ text }) {
  return (
    <div data-testid="login-error" className="px-4 py-3 rounded-lg text-sm text-red-300"
         style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
      {text}
    </div>
  );
}
